import { differenceInCalendarDays } from 'date-fns';

/** Why a calendar date can't be picked (CMS-1853, rules from CMS-1247). */
export type UnavailableReason =
  | 'PAST'
  | 'ADVANCE_WINDOW'
  | 'BEFORE_START'
  | 'MAX_DURATION';

export type UnavailableReasonContext = {
  /** Earliest selectable date (today). */
  minDate?: Date;
  /** Latest selectable start date (today + advance purchase window). */
  maxStartDate?: Date | null;
  /** 'start' — picking a start date; 'end' — a start is chosen, picking the end. */
  phase: 'start' | 'end';
  /** The chosen start date (phase 'end' only). */
  start?: Date;
  /** Maximum trip length in days after the start. */
  duration?: number;
  mode: 'range' | 'annual';
};

/**
 * Classifies why a date is unavailable, or returns null for a selectable
 * date. The reason reflects the CURRENT selection phase: before a start is
 * chosen it explains past / advance-window; while picking the end it explains
 * before-start / max-duration. Must stay in lockstep with the picker's
 * disabled matchers so the explanation never disagrees with the greying.
 */
export function classifyUnavailableDate(
  date: Date,
  context: UnavailableReasonContext,
): UnavailableReason | null {
  const { minDate, maxStartDate, phase, start, duration, mode } = context;

  // The reason reflects the CURRENT phase (ticket edge case): while picking
  // the end, every date before the start — past dates included — explains
  // BEFORE_START; PAST / ADVANCE_WINDOW are start-picking explanations.
  if (phase === 'start' || mode === 'annual') {
    if (minDate && differenceInCalendarDays(date, minDate) < 0) {
      return 'PAST';
    }
    if (maxStartDate && differenceInCalendarDays(date, maxStartDate) > 0) {
      return 'ADVANCE_WINDOW';
    }
    return null;
  }

  if (start) {
    if (differenceInCalendarDays(date, start) < 0) {
      return 'BEFORE_START';
    }
    if (
      duration &&
      duration > 0 &&
      differenceInCalendarDays(date, start) > duration
    ) {
      return 'MAX_DURATION';
    }
  }

  return null;
}
